
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, BarChart3, Zap } from 'lucide-react';

const NumberAnalytics = () => {
  const [selectedGame, setSelectedGame] = useState('powerball');

  const analytics = {
    powerball: {
      hot: [
        { number: 23, frequency: 47, percentage: 18.5 },
        { number: 7, frequency: 45, percentage: 17.7 },
        { number: 31, frequency: 42, percentage: 16.5 },
        { number: 14, frequency: 40, percentage: 15.7 },
        { number: 42, frequency: 38, percentage: 14.9 }
      ],
      cold: [
        { number: 1, frequency: 12, percentage: 4.7 },
        { number: 44, frequency: 14, percentage: 5.5 },
        { number: 35, frequency: 16, percentage: 6.3 },
        { number: 22, frequency: 18, percentage: 7.1 },
        { number: 8, frequency: 19, percentage: 7.5 }
      ],
      overdue: [
        { number: 13, lastSeen: '47 draws ago', percentage: 85 },
        { number: 29, lastSeen: '32 draws ago', percentage: 67 },
        { number: 18, lastSeen: '28 draws ago', percentage: 58 },
        { number: 5, lastSeen: '24 draws ago', percentage: 52 },
        { number: 39, lastSeen: '21 draws ago', percentage: 45 }
      ]
    },
    ozlotto: {
      hot: [
        { number: 17, frequency: 52, percentage: 20.5 },
        { number: 33, frequency: 49, percentage: 19.3 },
        { number: 11, frequency: 46, percentage: 18.1 },
        { number: 28, frequency: 44, percentage: 17.3 },
        { number: 2, frequency: 41, percentage: 16.1 }
      ],
      cold: [
        { number: 45, frequency: 8, percentage: 3.1 },
        { number: 6, frequency: 11, percentage: 4.3 },
        { number: 21, frequency: 13, percentage: 5.1 },
        { number: 37, frequency: 15, percentage: 5.9 },
        { number: 14, frequency: 17, percentage: 6.7 }
      ],
      overdue: [
        { number: 40, lastSeen: '41 draws ago', percentage: 82 },
        { number: 9, lastSeen: '35 draws ago', percentage: 70 },
        { number: 26, lastSeen: '29 draws ago', percentage: 58 },
        { number: 4, lastSeen: '25 draws ago', percentage: 50 },
        { number: 43, lastSeen: '22 draws ago', percentage: 44 }
      ]
    }
  };

  const currentAnalytics = analytics[selectedGame as keyof typeof analytics];

  return (
    <section className="py-16">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Number Analytics</h2>
          <p className="text-xl text-gray-700">Discover patterns and trends in lottery numbers</p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex gap-2">
            <Button
              variant={selectedGame === 'powerball' ? 'default' : 'outline'}
              onClick={() => setSelectedGame('powerball')}
              className={selectedGame === 'powerball' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
            >
              PowerBall
            </Button>
            <Button
              variant={selectedGame === 'ozlotto' ? 'default' : 'outline'}
              onClick={() => setSelectedGame('ozlotto')}
              className={selectedGame === 'ozlotto' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
            >
              Oz Lotto
            </Button>
          </div>
        </div>

        <Tabs defaultValue="frequency" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="frequency">Hot & Cold</TabsTrigger>
            <TabsTrigger value="overdue">Overdue Numbers</TabsTrigger>
            <TabsTrigger value="patterns">Patterns</TabsTrigger>
          </TabsList>
          
          <TabsContent value="frequency">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-red-500" />
                    Hot Numbers
                    <span className="text-sm font-normal text-gray-600">(Most Drawn)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {currentAnalytics.hot.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center font-bold">
                            {item.number}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">#{index + 1} Most Drawn</p>
                            <p className="text-sm text-gray-600">{item.frequency} times</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-red-600">{item.percentage}%</p>
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-red-500 transition-all"
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingDown className="w-6 h-6 text-blue-500" />
                    Cold Numbers
                    <span className="text-sm font-normal text-gray-600">(Least Drawn)</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {currentAnalytics.cold.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                            {item.number}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">#{index + 1} Least Drawn</p>
                            <p className="text-sm text-gray-600">{item.frequency} times</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-blue-600">{item.percentage}%</p>
                          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 transition-all"
                              style={{ width: `${item.percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="overdue">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-600" />
                  Most Overdue Numbers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {currentAnalytics.overdue.map((item, index) => (
                    <div key={index} className="p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3">
                          {item.number}
                        </div>
                        <p className="font-bold text-gray-900 mb-1">{item.lastSeen}</p>
                        <p className="text-sm text-gray-600 mb-2">Last appearance</p>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-yellow-600 transition-all"
                            style={{ width: `${item.percentage}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{item.percentage}% overdue</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> Past results do not influence future draws. 
                    Each draw is random and independent. These statistics are for entertainment purposes only.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="patterns">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-yellow-600" />
                    Number Ranges
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">Low Numbers (1-15)</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 w-3/5"></div>
                        </div>
                        <span className="text-sm text-gray-600">32%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">Mid Numbers (16-30)</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-yellow-500 w-2/3"></div>
                        </div>
                        <span className="text-sm text-gray-600">38%</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">High Numbers (31-45)</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 w-1/3"></div>
                        </div>
                        <span className="text-sm text-gray-600">30%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Consecutive Numbers</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">No Consecutive</span>
                      <span className="text-sm text-gray-600">65%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">One Pair</span>
                      <span className="text-sm text-gray-600">28%</span>
                    </div>
                    <div className="flex justify-between items-center p-3 border rounded-lg">
                      <span className="font-medium">Two+ Pairs</span>
                      <span className="text-sm text-gray-600">7%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default NumberAnalytics;
